import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import SchemeModal from './SchemeModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { format } from 'date-fns';

const { FiPlus, FiEdit2, FiLayers, FiCalendar, FiRefreshCw, FiImage } = FiIcons;

const Schemes = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);

  const { data: schemes = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['schemes'],
    queryFn: async () => {
      const res = await api.get('get_schemes.php');
      return res.data?.data || [];
    }
  });

  const handleEdit = (scheme) => {
    setSelectedScheme(scheme);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedScheme(null);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-surface-900 uppercase tracking-tight">All Campaigns</h2>
          <p className="text-xs text-surface-400 font-bold uppercase tracking-[0.2em] mt-1">Manage and configure your rewards programs</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching} className="h-12 w-12 rounded-2xl border-white bg-white shadow-sm">
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={handleCreate} className="h-12 px-8 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white shadow-xl shadow-primary-200">
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            <span className="text-xs font-black uppercase tracking-widest">Create New</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-[2.5rem]" />)
        ) : schemes.length === 0 ? (
          <div className="col-span-full py-32 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-surface-200">
            <div className="w-20 h-20 bg-surface-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiLayers} className="w-10 h-10 text-surface-200" />
            </div>
            <h3 className="text-lg font-black text-surface-900 uppercase">No Campaigns Found</h3>
            <p className="text-sm text-surface-400 mt-2 font-medium">Start by creating your first reward scheme</p>
          </div>
        ) : (
          schemes.map((scheme) => (
            <Card key={scheme.id} className="group relative overflow-hidden border-none shadow-card hover:shadow-card-hover transition-all duration-500 rounded-[2.5rem] bg-white">
              <div className="absolute top-6 right-6 z-10">
                <Badge variant={scheme.status === 'active' ? 'success' : 'neutral'} className="font-black text-[9px] uppercase px-3 py-1 rounded-full border-2 border-white shadow-sm">
                  {scheme.status}
                </Badge>
              </div>

              <div className="h-24 bg-surface-50 relative overflow-hidden">
                {scheme.campaign_image ? (
                  <img src={scheme.campaign_image} alt={scheme.scheme_name} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <SafeIcon icon={FiImage} className="w-8 h-8 text-surface-200" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
              </div>

              <CardContent className="p-8 pt-2">
                <div className="mb-6">
                  <span className="text-[9px] font-black text-primary-500 uppercase tracking-widest">ID: {scheme.id}</span>
                  <h3 className="text-lg font-black text-surface-900 truncate mt-1">{scheme.scheme_name}</h3>
                  <p className="text-xs font-bold text-surface-400 mt-0.5">{scheme.brand_name || 'Generic Brand'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="space-y-1">
                    <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest">Launch</p>
                    <div className="flex items-center gap-2 text-surface-600">
                      <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                      <span className="text-[11px] font-bold">{scheme.start_date ? format(new Date(scheme.start_date), 'MMM dd, yy') : 'Open'}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest">Expires</p>
                    <div className="flex items-center gap-2 text-surface-600">
                      <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                      <span className="text-[11px] font-bold">{scheme.end_date ? format(new Date(scheme.end_date), 'MMM dd, yy') : 'Never'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => handleEdit(scheme)} 
                    className="w-full h-11 rounded-xl border-surface-200 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 text-[10px] font-black uppercase tracking-widest"
                  >
                    <SafeIcon icon={FiEdit2} className="w-3 h-3 mr-2" />
                    Edit Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <SchemeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} scheme={selectedScheme} />
    </div>
  );
};

export default Schemes;